import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Box, Switch, FormControlLabel, Tooltip, IconButton, Snackbar } from '@mui/material';
import MuiAlert from "@mui/material/Alert";
import { alpha, styled } from '@mui/material/styles';
import { green } from '@mui/material/colors';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DetailsPageLowerLayer from '../../DetailsPageLowerLayer/DetailsPageLowerLayer';
import Toolbar from '../../../CommonComponents/common/toolbar/Toolbar';
import { fetchAllReports, handleUserAccess } from '../../../services/UserRole/ReportsService';
import { APPLICATION_TYPE_MAP_REPORT } from '../../../constants/userAccess';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const SOP_APPLICATION_TYPE = "SALES_AND_OPERATIONAL_PLANNING";

const normalizeLocationValue = (...values) => {
    const value = values.find((entry) => {
        if (entry === undefined || entry === null || entry === "") return false;
        if (Array.isArray(entry)) return entry.length > 0;
        if (typeof entry === "object") return Object.keys(entry).length > 0;
        return true;
    });
    const flatten = (entry) => {
        if (Array.isArray(entry)) return entry.flatMap(flatten);
        if (entry && typeof entry === "object") return Object.values(entry).flatMap(flatten);
        return entry === undefined || entry === null || entry === "" ? [] : [entry];
    };

    return flatten(value).join(", ");
};

const CSV_COLUMNS = [
    { key: 'region', label: 'Region' },
    { key: 'mrPlant', label: 'MR Plant', app: 'NON_SOP' },
    { key: 'country', label: 'Country', app: 'SOP' },
    { key: 'bottler', label: 'Bottler', app: 'SOP' },
    { key: 'restrictedAccess', label: 'Restricted Access' },
    { key: 'accessEnabledDate', label: 'Access Enabled Date' },
    { key: 'ndaURL', label: 'NDA Signed', app: 'COMMANDCENTER' },
    { key: 'role', label: 'Role', app: 'NON_COMMANDCENTER' }
];

const GreenSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
        color: green[600],
        '&:hover': {
            backgroundColor: alpha(green[600], theme.palette.action.hoverOpacity),
        },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: green[600],
    },
}));

const NdaLinkRenderer = (props) => {
    const url = props.value;
    return url !== "N/A" ? (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer"
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {url}
        </a>) : <span>{url}</span>
};

const downloadCsv = (rows, appValue) => {
    const activeColumns = CSV_COLUMNS.filter(col => {
        if (col.app === 'COMMANDCENTER') return appValue === 'COMMANDCENTER';
        if (col.app === 'NON_COMMANDCENTER') return appValue !== 'COMMANDCENTER';
        if (col.app === 'SOP') return appValue === SOP_APPLICATION_TYPE;
        if (col.app === 'NON_SOP') return appValue !== SOP_APPLICATION_TYPE;
        return true;
    });
    const headers = activeColumns.map(col => col.label);

    const csvRows = rows.map(row =>
        activeColumns.map(({ key }) => {
            let value = row[key];

            if (Array.isArray(value)) {
                value = value.join('; ');
            }

            if (value === undefined || value === null) {
                value = '';
            }
            return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
    );
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `user-access-report_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
};

const UserSpecificDetails = ({ application, userName, userEmail, onBack }) => {
    const appValue = APPLICATION_TYPE_MAP_REPORT[application];
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [selectionMode, setSelectionMode] = useState(null);
    const [toggleLoading, setToggleLoading] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const hasSelection = selectionModel.length > 0;
    const enableButtonDisabled = !hasSelection || selectionMode === true; // enabled rows selected
    const disableButtonDisabled = !hasSelection || selectionMode === false; // disabled rows selected
    // true = enabled rows, false = disabled rows

    const handleSelectionChange = useMemo(() => (params) => {
        const selectedRows = params.api.getSelectedRows();

        //Reset when nothing is selected
        if (selectedRows.length === 0) {
            setSelectionModel([]);
            setSelectionMode(null);
            return;
        }

        // First selection locks the mode
        if (selectionMode === null) {
            setSelectionMode(selectedRows[0].active);
            setSelectionModel(selectedRows.map(r => r.id));
            return;
        }

        // Enforce same enabled/disabled selection
        selectedRows.forEach(row => {
            if (row.active !== selectionMode) {
                params.api.getRowNode(row.id)?.setSelected(false);
            }
        });

        // Sync local selection model
        const validIds = params.api
            .getSelectedRows()
            .map(r => r.id);

        setSelectionModel(validIds);
    }, [selectionMode]);

    const handleBack = () => {
        if (onBack) {
            onBack();
        }
    };

    const refreshReports = useCallback(async () => {
        try {
            setLoading(true);
            await delay(1500);
            const data = await fetchAllReports(
                "USER_SPECIFIC",
                userEmail,
                APPLICATION_TYPE_MAP_REPORT[application]
            );
            setRows(normalizeApiRows(data));
        } catch (err) {
            console.error("Failed to refresh reports", err);
            setSnackbar({
                open: true,
                severity: "error",
                message: "Failed to fetch reports"
            });
        } finally {
            setLoading(false);
        }
    }, [application, userEmail]);

    const normalizeApiRows = (data = []) =>
        data.map((item, index) => ({
            id: index + 1,
            userName: item.userName,
            userEmail: item.userEmail,
            region: item.region,
            mrPlant: item.plant,
            country: normalizeLocationValue(
                item.country, item.countries, item.countryList, item.countryName, item.countryNames
            ),
            bottler: normalizeLocationValue(
                item.bottler, item.bottlers, item.bottlerList, item.bottlerGroup,
                item.bottlerGroups, item.bottlerName, item.bottlerNames
            ),
            restrictedAccess: item.restrictedAccess,
            role: item.role,
            ndaURL: item.ndaUrl || "N/A",
            accessEnabledDate: item.enabledDate,
            active: item.active
        }));

    const handleToolbarToggle = async (isActive) => {
        if (!selectionModel.length) return;

        const selectedUsers = rows.filter(row =>
            selectionModel.includes(row.id)
        );

        const payload = selectedUsers.map(user => ({
            username: user.userName,
            userEmail: user.userEmail,
            restrictedAccess: user.restrictedAccess,
            role: user.role,
            region: user.region,
            plant: user.mrPlant,
            ...(appValue === SOP_APPLICATION_TYPE && { country: user.country, bottler: user.bottler }),
            applicationType: appValue,
        }));

        try {
            const response = await handleUserAccess(isActive, payload);
            await refreshReports();
            setSelectionModel([]);
            setSelectionMode(null);
            if (response?.statusCode === 200) {
                setSnackbar({
                    open: true,
                    severity: 'success',
                    message: response.message || 'Access updated successfully'
                });
            }
        } catch (error) {
            console.error("Toolbar enable/disable failed", error);
            setSnackbar({
                open: true,
                message: `Failed to ${isActive ? "enable" : "disable"} access`,
                severity: 'error'
            });
        }
    };

    useEffect(() => {
        const fetchReports = async () => {
            if (!application) return;
            try {
                setLoading(true);
                const data = await fetchAllReports("USER_SPECIFIC", userEmail, APPLICATION_TYPE_MAP_REPORT[application]);
                setRows(normalizeApiRows(data));
            } catch (err) {
                console.error("Failed to fetch reports", err);
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: "Failed to fetch reports"
                });
            } finally {
                setLoading(false);
            }
        };
        fetchReports();
    }, [application, userEmail]);

    const enableDisableActions = (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center' }}>
            <Tooltip title="Enable selected users" placement="top" arrow>
                <IconButton disabled={enableButtonDisabled}
                    sx={{ color: '#000', paddingRight: 0 }}
                    onClick={() => handleToolbarToggle(true)}
                >
                    <ToggleOnIcon fontSize="large" color="inherit" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Disable selected users" placement="top" arrow>
                <IconButton disabled={disableButtonDisabled}
                    sx={{ color: '#000', paddingRight: 0 }}
                    onClick={() => handleToolbarToggle(false)}
                >
                    <ToggleOffIcon fontSize="large" color="inherit" />
                </IconButton>
            </Tooltip>
        </Box>
    );
    const toolbarProps = {
        onBack: handleBack,
        rightIcon: <FileDownloadOutlinedIcon />,
        tooltipText: "Click to download Report",
        downloadAction: () => downloadCsv(rows, appValue),
        toolbarMeta: {
            heading: application,
            metaLine: {
                userName,
                userEmail
            }
        },
        toolbarActions: enableDisableActions
    };

    const baseColumns = useMemo(() => [
        {
            headerName: '',
            field: 'checkbox',
            maxWidth: 40,
            checkboxSelection: true,
        },
        { field: "region", headerName: "Region", flex: 0.8 },
        ...(appValue === SOP_APPLICATION_TYPE
            ? [
                { field: "country", headerName: "Country", flex: 0.8 },
                { field: "bottler", headerName: "Bottler", flex: 0.8 },
            ]
            : [{ field: "mrPlant", headerName: "MR Plant", flex: 0.7 }]),

        {
            field: "accessEnabledDate",
            headerName: "Access Enabled Date",
            flex: 1,
        },
    ], [appValue]);

    // const [rows, setRows] = useState([
    //     {
    //         id: 1,
    //         userName: "Nikhil",
    //         userEmail: "nrathi@coca-cola.com",
    //         region: "EMEA",
    //         mrPlant: "0230",
    //         restrictedAccess: "Inventory",
    //         ndaURL: "<url>",
    //         enabled: true,      // ✅
    //         accessEnabledDate: "2024-01-15",
    //         application: "Command Center"
    //     },
    //     {
    //         id: 2,
    //         userName: "Nikhil",
    //         userEmail: "nrathi@coca-cola.com",
    //         region: "AMERICAS",
    //         mrPlant: "0777",
    //         restrictedAccess: "Inventory",
    //         ndaURL: "<url>",
    //         enabled: true,      // ✅
    //         accessEnabledDate: "2024-02-20",
    //         application: "Command Center"
    //     },
    //     {
    //         id: 3,
    //         userName: "Kriti",
    //         userEmail: "krsinghal@coca-cola.com",
    //         region: "EMEA",
    //         mrPlant: "5040",
    //         restrictedAccess: "Inventory",
    //         ndaURL: "<url>",
    //         accessEnabledDate: "2024-03-10",
    //         enabled: false,     // ✅
    //         application: "Command Center"
    //     },
    //     {
    //         id: 4,
    //         userName: "Kriti",
    //         userEmail: "krsinghal@coca-cola.com",
    //         region: "APAC",
    //         mrPlant: "0201",
    //         restrictedAccess: "Inventory",
    //         ndaURL: "<url>",
    //         accessEnabledDate: "2024-04-10",
    //         enabled: true,     // ✅,
    //         application: "Command Center"
    //     },
    //     {
    //         id: 5,
    //         userName: "Abdul",
    //         userEmail: "absayed@coca-cola.com",
    //         region: "AMERICAS",
    //         mrPlant: "0999",
    //         restrictedAccess: "Inventory",
    //         ndaURL: "<url>",
    //         enabled: true,     // ✅
    //         accessEnabledDate: "2024-05-05",
    //         role: "Business Super User",
    //         application: "SSA"
    //     },
    //     {
    //         id: 6,
    //         userName: "Abdul",
    //         userEmail: "absayed@coca-cola.com",
    //         region: "EMEA",
    //         mrPlant: "7777",
    //         restrictedAccess: "Inventory",
    //         ndaURL: "<url>",
    //         enabled: false,     // ✅
    //         accessEnabledDate: "2024-06-01",
    //         role: "Business Super User",
    //         application: "SSA"
    //     }
    // ]);

    // const filteredRows = useMemo(() => {
    //     let filtered;
    //     if (application === "SSA") {
    //         filtered = rows.filter(row => row.application === "SSA");
    //     }

    //     if (application === "Command Center") {
    //         filtered = rows.filter(row => row.application === "Command Center");
    //     }
    //     filtered = filtered.filter((row) => row.userEmail === userEmail);
    //     return filtered; // fallback
    // }, [rows, application, userEmail]);

    const conditionalColumns = useMemo(() => {
        if (appValue === "SSA") {
            return [
                {
                    field: "role",
                    headerName: "Role",
                    flex: 0.6
                },
            ];
        }
        if (appValue === "COMMANDCENTER") {
            return [
                {
                    field: "restrictedAccess",
                    headerName: "Restricted Access",
                    flex: 1,
                },
                {
                    field: "ndaURL",
                    headerName: "NDA Signed",
                    flex: 1,
                    cellRenderer: NdaLinkRenderer,
                },
            ];
        }
        return [];
    }, [appValue]);

    const actionColumn = useMemo(() => ({
        field: "active",
        headerName: "Action",
        minWidth: 220,
        flex: 1.5,
        filter: "agSetColumnFilter",
        filterParams: {
            values: [true, false],

            valueFormatter: (params) => {
                return params.value === true
                    ? "Enable"
                    : "Disable";
            },

            buttons: ["apply", "clear"],
            closeOnApply: true,
        },

        cellRenderer: (params) => {
            const { id, active } = params.data;
            const isSwitchDisabled = hasSelection || toggleLoading === id;

            const handleToggle = async (e) => {
                e.stopPropagation();
                if (isSwitchDisabled) return;

                const newActiveState = e.target.checked;
                // Optimistic UI update
                setRows(prev =>
                    prev.map(row =>
                        row.id === id
                            ? { ...row, active: newActiveState }
                            : row
                    )
                );
                setToggleLoading(id);

                try {
                    const payload = [
                        {
                            username: params.data.userName,
                            userEmail: params.data.userEmail,
                            restrictedAccess: params.data.restrictedAccess,
                            role: params.data.role,
                            region: params.data.region,
                            plant: params.data.mrPlant,
                            ...(appValue === SOP_APPLICATION_TYPE && { country: params.data.country, bottler: params.data.bottler }),
                            applicationType: APPLICATION_TYPE_MAP_REPORT[application],
                        }
                    ];

                    const response = await handleUserAccess(
                        !active,
                        payload,
                    );
                    if (response?.statusCode === 200) {
                        setSnackbar({
                            open: true,
                            severity: 'success',
                            message: response.message || 'Access updated successfully'
                        });
                    }
                } catch (error) {
                    console.error("Toggle failed", error);
                    setSnackbar({
                        open: true,
                        severity: 'error',
                        message: `Failed to ${!active ? "enable" : "disable"} access`
                    });
                    // Revert UI on API failure
                    setRows(prev =>
                        prev.map(row =>
                            row.id === id
                                ? { ...row, active: !newActiveState }
                                : row
                        )
                    );
                } finally {
                    await refreshReports();
                    setToggleLoading(null);
                }
            };
            return (
                <FormControlLabel
                    control={
                        <GreenSwitch
                            checked={active}
                            disabled={isSwitchDisabled}
                            onChange={handleToggle}
                        />
                    }
                    label={`Click to ${active ? "Disable" : "Enable"}`}
                    sx={{
                        opacity: isSwitchDisabled ? 0.5 : 1,
                        cursor: isSwitchDisabled ? "not-allowed" : "pointer",
                        '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            backgroundColor: isSwitchDisabled ? '#eaaaee' : '#7a7a7a',
                            borderRadius: '4px',
                            color: '#fff',
                            marginLeft: '5px',
                            paddingX: '4px',
                        },
                    }}
                />
            );
        },
    }), [hasSelection, toggleLoading, application, refreshReports]);

    const columns = useMemo(() => [
        ...baseColumns,
        ...conditionalColumns,
        actionColumn,
    ], [baseColumns, conditionalColumns, actionColumn]);

    const reportTab = useMemo(() => ({
        label: "Details",
        loading,
        columns,
        rows,
        onSelectionChanged: handleSelectionChange,
        selectionMode
    }), [columns, rows, loading, handleSelectionChange, selectionMode]);

    const tabs = [reportTab];

    return (
        <>
            <Toolbar {...toolbarProps} />
            <DetailsPageLowerLayer tabs={tabs} hideTabs />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    //onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default UserSpecificDetails;
